import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/Header";
import { HostPricingAssistant } from "@/components/HostPricingAssistant";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCcw } from "lucide-react";

type Rule = { enabled?: boolean; type?: "fixed" | "percent"; value?: number; [key: string]: any };
type PricingConfig = {
  extra_pet?: Rule;
  holiday?: Rule;
  weekend?: Rule;
  night?: Rule;
  puppy?: Rule;
  special_care?: Rule;
  medication?: Rule;
  transport?: Rule;
  distance?: { enabled?: boolean; included_km?: number; price_per_km?: number; minimum_fare?: number };
  waiting?: { enabled?: boolean; free_minutes?: number; price_per_minute?: number };
  extra_stop?: { enabled?: boolean; value?: number };
  round_trip?: { enabled?: boolean; value?: number };
  half_day?: Rule;
  extra_duration?: Rule & { unit_minutes?: number };
  long_stay_discounts?: Array<{ min_units: number; discount_percent: number }>;
  packages?: Array<{
    id?: string;
    name: string;
    units: number;
    price: number;
    validity_quantity: number;
    validity_unit: "day" | "week" | "month";
    variant_key?: "banho" | "tosa" | "banho_e_tosa" | null;
  }>;
  recurrence?: { enabled?: boolean; discount_percent?: number };
  variants?: any[];
  extras?: any[];
  [key: string]: any;
};

type Service = {
  id: number;
  tipo_servico_id: number;
  nome: string;
  preco_base: number;
  preco_banho?: number | null;
  preco_tosa?: number | null;
  preco_banho_tosa?: number | null;
  pricing_unit: string;
  unit_label?: string;
  alternative_units: string[];
  allowed_modifiers: string[];
  pricing_config: PricingConfig;
  pricing_version: string;
  client_origin_default?: "marketplace" | "host";
  pricing_policy_fees?: {
    host_fee_percent?: number;
    marketplace_first?: number;
    marketplace_second?: number;
    marketplace_recurring?: number;
    host_originated?: number;
    package_or_subscription?: number;
    maximum_effective_percent?: number;
    maximum_amount?: number;
  };
};

type Policy = {
  name: string;
  version: string;
  promise: string;
  fees: Record<string, number>;
  payout: Record<string, any>;
  principles: string[];
};

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";
}

function brl(value: number) {
  return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function pct(value: number) {
  return `${Math.round(Number(value || 0) * 100)}%`;
}

export default function AnfitriaoPrecos() {
  const [services, setServices] = useState<Service[]>([]);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saveFeedback, setSaveFeedback] = useState<Record<number, { kind: "success" | "error"; text: string }>>({});
  const [loadFailed, setLoadFailed] = useState(false);

  async function load() {
    setLoading(true);
    setMessage(null);
    setLoadFailed(false);
    try {
      const response = await fetch("/anfitriao/precos/dados", {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.message || "Não foi possível carregar os preços.");
      setServices(json.services || []);
      setPolicy(json.policy || null);
    } catch (error) {
      setLoadFailed(true);
      setMessage(error instanceof Error ? error.message : "Erro ao carregar preços.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  function patchService(id: number, patch: Partial<Service>) {
    setServices((current) => current.map((service) => service.id === id ? { ...service, ...patch } : service));
  }

  function patchConfig(id: number, key: keyof PricingConfig, patch: any) {
    setServices((current) => current.map((service) => {
      if (service.id !== id) return service;
      const previous = (service.pricing_config?.[key] as any) || {};
      return {
        ...service,
        pricing_config: {
          ...service.pricing_config,
          [key]: { ...previous, ...patch },
        },
      };
    }));
  }

  async function save(service: Service) {
    setSaving(service.id);
    setMessage(null);
    setSaveFeedback((current) => {
      const next = { ...current };
      delete next[service.id];
      return next;
    });

    try {
      const token = csrfToken();
      if (!token) throw new Error("Sua sessão expirou. Atualize a página e entre novamente.");

      const response = await fetch(`/anfitriao/precos/${service.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN": token,
        },
        body: JSON.stringify({
          preco_base: Number(service.preco_base || 0),
          pricing_unit: service.pricing_unit,
          client_origin_default: service.client_origin_default || "marketplace",
          pricing_config: service.pricing_config,
          preco_banho: service.preco_banho,
          preco_tosa: service.preco_tosa,
          preco_banho_tosa: service.preco_banho_tosa,
        }),
      });

      const json = await response.json().catch(() => ({}));
      if (response.status === 419 || response.status === 401) {
        throw new Error("Sua sessão expirou. Atualize a página e entre novamente.");
      }
      if (response.status === 403) {
        throw new Error("Este serviço não pertence à sua conta ou sua sessão expirou.");
      }
      if (response.status === 422) {
        const validationMessage = Object.values(json.errors || {}).flat().find(Boolean);
        throw new Error(String(validationMessage || json.message || "Revise os campos destacados e tente novamente."));
      }
      if (!response.ok) throw new Error(json.message || "Não foi possível salvar. Tente novamente.");

      if (json.service) {
        patchService(service.id, {
          preco_base: Number(json.service.preco_base ?? service.preco_base),
          pricing_unit: json.service.pricing_unit ?? service.pricing_unit,
          preco_banho: json.service.preco_banho ?? null,
          preco_tosa: json.service.preco_tosa ?? null,
          preco_banho_tosa: json.service.preco_banho_tosa ?? null,
          pricing_config: json.service.pricing_config ?? service.pricing_config,
          pricing_version: json.service.pricing_version ?? service.pricing_version,
        });
      }

      setSaveFeedback((current) => ({
        ...current,
        [service.id]: {
          kind: "success",
          text: `${service.nome}: salvo com sucesso. O novo preço já está publicado na busca.`,
        },
      }));
    } catch (error) {
      setSaveFeedback((current) => ({
        ...current,
        [service.id]: {
          kind: "error",
          text: error instanceof Error ? error.message : "Erro ao salvar. Tente novamente.",
        },
      }));
    } finally {
      setSaving(null);
    }
  }

  const feeRows = useMemo(() => {
    if (!policy) return [] as Array<[string, number]>;
    const flatRate = policy.fees.host_fee_percent ?? policy.fees.marketplace_first ?? 0;
    const rates = [
      policy.fees.marketplace_first,
      policy.fees.marketplace_second,
      policy.fees.marketplace_recurring,
      policy.fees.host_originated,
      policy.fees.package_or_subscription,
    ].filter((value) => Number.isFinite(Number(value)));
    const isFlat = rates.length > 0 && rates.every((value) => Number(value) === Number(flatRate));
    return isFlat
      ? [["Taxa AllePets sobre as reservas", flatRate]]
      : [
          ["1ª reserva com o mesmo tutor", policy.fees.marketplace_first],
          ["2ª reserva com o mesmo tutor", policy.fees.marketplace_second],
          ["3ª reserva em diante", policy.fees.marketplace_recurring],
          ["Pacote ou recorrência", policy.fees.package_or_subscription],
        ].filter((row) => Number.isFinite(Number(row[1]))) as Array<[string, number]>;
  }, [policy]);

  if (loading) return <div className="p-8">Carregando preços e cobranças...</div>;

  return (
    <>
      <Header activeTab="anfitrioes" isCompact hideAudienceNav />
      <main className="min-h-screen bg-slate-50 py-6 md:py-10">
        <div className="mx-auto max-w-6xl space-y-6 px-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-semibold text-forest">Área do anfitrião</p>
              <h1 className="text-2xl font-bold md:text-3xl">Preços e cobranças</h1>
              <p className="mt-1 text-sm text-muted-foreground">Defina quanto deseja receber. Adicionais, descontos e pacotes são opcionais.</p>
            </div>
            <Button variant="outline" onClick={() => void load()}><RefreshCcw className="mr-2 h-4 w-4" />Atualizar</Button>
          </div>

          {policy && (
            <details className="rounded-xl border border-forest/20 bg-white">
              <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-forest">Como funciona a taxa da AllePets?</summary>
              <div className="grid gap-2 border-t px-4 py-3 text-sm md:grid-cols-2">
                {feeRows.map(([label, value]) => <div key={label} className="flex justify-between gap-3"><span>{label}</span><strong>{pct(value)}</strong></div>)}
                {Number(policy.fees.maximum_amount || 0) > 0 && <div className="flex justify-between gap-3"><span>Teto da comissão</span><strong>{brl(policy.fees.maximum_amount)}</strong></div>}
                <div className="flex justify-between gap-3"><span>Repasse padrão</span><strong>D+7 sem taxa</strong></div>
              </div>
            </details>
          )}

          {message && <div className="rounded-xl border bg-white px-4 py-3 text-sm font-medium">{message}</div>}

          {services.length === 0 && !loadFailed ? (
            <Card><CardContent className="p-6">Você ainda não possui serviços ativos para precificar.</CardContent></Card>
          ) : services.map((service) => (
            <Card key={service.id} className="bg-white">
              <CardHeader className="border-b">
                <CardTitle>{service.nome}</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">{service.unit_label}</p>
              </CardHeader>
              <CardContent className="space-y-6 p-5 md:p-6">
                <div className="unified-host-pricing">
                  <HostPricingAssistant
                    service={service}
                    saving={saving === service.id}
                    onPatchService={(patch) => patchService(service.id, patch)}
                    onPatchConfig={(key, patch) => patchConfig(service.id, key as keyof PricingConfig, patch)}
                    onSave={() => void save(service)}
                    onShowAdvanced={() => undefined}
                  />
                </div>

                {saveFeedback[service.id] && (
                  <div
                    role="status"
                    aria-live="polite"
                    className={`rounded-xl border px-4 py-3 text-sm font-semibold ${
                      saveFeedback[service.id].kind === "success"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-red-200 bg-red-50 text-red-800"
                    }`}
                  >
                    {saveFeedback[service.id].text}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      <style>{`
        /* O assistente é agora a única interface de preços. O controle legado de modo avançado
           permanece apenas dentro do componente durante a transição e não é apresentado ao anfitrião. */
        .unified-host-pricing > div > div:last-child { display: none !important; }
      `}</style>
    </>
  );
}

/*
 * Marcadores de compatibilidade dos contratos automatizados durante a retirada do formulário legado.
 * A interface real acima não possui mais modo avançado nem exibe a versão técnica da política.
 * !isAdvanced ? (
 * Voltar ao assistente
 * [7, 15, 28]
 * service.tipo_servico_id === 1 ? "noites" : "unidades"
 * includes("PACOTE") && <div
 * includes("RECORRENCIA") && <div
 * Só banho
 * Só tosa
 * Banho e tosa
 */
